import { gql, useQuery as useApolloQuery } from '@apollo/client';
import {
  buildClientSchema,
  getIntrospectionQuery,
  GraphQLObjectType,
  GraphQLSchema,
  IntrospectionQuery,
} from 'graphql';
import React, { useEffect } from 'react';
import { DynamicTableWidget } from '~/components/ui/widgets/DynamicTableWidget/DynamicTableWidget';
import { ColumnConfig } from '~/lib/table/generateAgGridColumns';

export interface DynamicTableWidgetProps {
  query: string;
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
  key: string;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.prototype.toString.call(value) === '[object Object]';

export const getFieldPaths = (
  obj: Record<string, unknown>,
  parentKey = '',
  depth = 0,
  maxDepth = 2,
): string[] => {
  if (!isPlainObject(obj) || depth > maxDepth) return [];

  return Object.entries(obj).flatMap(([key, value]) => {
    if (key === '__typename') return [];
    const path = parentKey ? `${parentKey}.${key}` : key;
    if (isPlainObject(value)) {
      return getFieldPaths(value, path, depth + 1, maxDepth);
    }
    return [path];
  });
};

export const getFieldTypeFromSchema = (
  fieldName: string,
  parentTypeName: string,
  schema: GraphQLSchema,
): ColumnConfig['type'] => {
  if (!schema) return 'string';
  const type = schema.getType(parentTypeName);
  if (type instanceof GraphQLObjectType) {
    const field = type.getFields()[fieldName];
    if (field) {
      const gqlType = field.type.toString();
      if (gqlType.includes('Int') || gqlType.includes('Float')) return 'number';
      if (gqlType.includes('String')) return 'string';
      if (gqlType.includes('Date')) return 'date';
      // Add more mappings as needed
    }
  }
  return 'string';
};

export const DynamicTableWidgetWrapper = ({
  query,
  widgetId,
  handleRemoveWidget,
  shortDescription,
}: DynamicTableWidgetProps) => {
  const dataQuery = gql`
    ${query}
  `;
  const { data, loading, error, refetch } = useApolloQuery(dataQuery, {
    notifyOnNetworkStatusChange: true,
  });
  const introspectionQuery = getIntrospectionQuery();
  const introspectionResult = useApolloQuery(gql`
    ${introspectionQuery}
  `);

  const [columns, setColumns] = React.useState<ColumnConfig[] | null>(null);
  const [columnsLoading, setColumnsLoading] = React.useState(true);

  // Generate columns asynchronously when data and schema are ready
  useEffect(() => {
    const generateColumns = async () => {
      setColumnsLoading(true);
      if (loading || !data) {
        setColumns(null);
        setColumnsLoading(false);
        return;
      }
      // 1. Get the main data array (e.g., data.PortfolioCompany)
      const mainKey = data ? Object.keys(data)[0] : undefined;
      const tableData = mainKey ? data[mainKey] : [];
      // 2. Build schema if available
      let schema: GraphQLSchema | undefined = undefined;
      if (introspectionResult && introspectionResult.data) {
        try {
          schema = buildClientSchema(
            introspectionResult.data as IntrospectionQuery,
          );
        } catch {}
      }
      // 3. Generate columns dynamically, supporting nested fields
      let generatedColumns: ColumnConfig[] = [];
      if (tableData && tableData.length > 0 && mainKey && schema) {
        generatedColumns = getFieldPaths(tableData[0]).map((key) => ({
          field: key,
          headerName: key
            .split('.')
            .map((part) =>
              part
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase()),
            )
            .join(' '),
          type: getFieldTypeFromSchema(
            key.split('.').pop() || key,
            mainKey,
            schema,
          ),
        }));
      } else if (tableData && tableData.length > 0) {
        generatedColumns = getFieldPaths(tableData[0]).map((key) => ({
          field: key,
          headerName: key
            .split('.')
            .map((part) =>
              part
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase()),
            )
            .join(' '),
          type: 'string',
        }));
      }
      setColumns(generatedColumns);
      setColumnsLoading(false);
    };
    generateColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, introspectionResult]);

  if (loading || columnsLoading || !columns) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  // 1. Get the main data array (e.g., data.PortfolioCompany)
  const mainKey = data ? Object.keys(data)[0] : undefined;
  const tableData = mainKey ? data[mainKey] : [];

  return (
    <DynamicTableWidget
      columns={columns}
      data={tableData}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      shortDescription={shortDescription}
    />
  );
};
